import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Box, Container, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
  InputText,
  InputMask,
  AutoComplete,
  CheckBox,
  Button,
  TermsAndConditions,
  PrivacyPolicy,
  ConfirmationModal,
  ConsentIframeModal,
  DatePicker,
} from '../../components';
import { useTranslation, useQuery } from '../../shared/hooks';
import * as translations from './intl';
import { Formik, Form } from 'formik';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import {
  maskedCellphone,
  maskedAccessCode,
} from '../../shared/constants/masks';
import { apiService } from '../../services';
import * as Yup from 'yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../shared/constants/globalConstants';
import DarkTitle from '../../components/DarkTitle';

const CONSENT_MESSAGE_TYPE = 'DOCXPRESSO_CONSENT_RESULT';

const normalizeConsentDocuments = (response) => {
  const payload = response?.data ?? response;
  const body = payload?.data ?? payload;
  const rawDocuments = Array.isArray(body)
    ? body
    : Array.isArray(body?.consents)
      ? body.consents
      : Array.isArray(body?.documents)
        ? body.documents
        : Array.isArray(body?.items)
          ? body.items
          : body
            ? [body]
            : [];

  const documents = rawDocuments
    .map((document, index) => ({
      id: document?.id || document?.document_id || `${index}`,
      documentType:
        document?.document_type ||
        document?.documentType ||
        document?.type ||
        `consent-${index + 1}`,
      url:
        document?.consent_url ||
        document?.consentUrl ||
        document?.url ||
        document?.document_url ||
        document?.documentUrl ||
        null,
    }))
    .filter((document) => document.url);

  return {
    clientId: body?.client_id || body?.clientId || null,
    documents,
  };
};

const formatConsentTitle = (documentType) =>
  documentType
    ? documentType
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Consent document';

const getStyles = (theme) => ({
  title: {
    fontFamily: 'Publico Headline',
    fontSize: 36,
    fontWeight: 700,
    [theme.breakpoints.down('md')]: {
      fontSize: 30,
    },
    lineHeight: '40px',
    color: theme.palette.text.main,
    paddingBottom: '42px',
    paddingTop: '42px',
  },
  gridSubTitle: {
    borderBottom: '1px solid #ccc',
    paddingTop: '20px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '30px',
    },
    [theme.breakpoints.down('lg')]: {
      marginBottom: '30px',
    },
  },
  subTitle: {
    fontFamily: 'Publico Headline',
    fontSize: 25,
    fontWeight: 700,
    lineHeight: '30px',
    letterSpacing: '0.8px',
    padding: '10px 50px',
    color: theme.palette.text.main,
  },
  containerInput: {
    padding: '0px 90px !important',
    paddingTop: '44px !important',
    [theme.breakpoints.down('lg')]: {
      padding: '0px 40px !important',
      paddingTop: '44px !important',
    },
    marginBottom: '20px',
  },
  paperContent: {
    width: '100%',
  },
  container: {
    marginBottom: '96px',
  },
  linkCheckbox: {
    fontWeight: 700,
    fontFamily: 'Source Sans Pro',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  textCheckbox: {
    color: theme.palette.text.main,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 16,
    lineHeight: '24px',
    fontFamily: 'Source Sans Pro',
    marginBottom: 0,
  },
  paddingFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '25px 29px',
    borderTop: '1px solid rgba(229, 229, 229, 1)',
  },
  gridCheckbox: {
    padding: '0 10px',
  },
  w100: {
    width: '100%',
  },
  chevronDown: {
    position: 'absolute',
    color: '#00008f',
    right: 0,
    pointerEvents: 'auto',
    fontSize: 16,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  inputDescription: {
    visibility: 'visible',
    fontSize: 15,
    fontWeight: 'normal',
    fontFamily: 'Source Sans Pro',
  },
  inputDescriptionHide: {
    position: 'absolute',
    visibility: 'hidden',
    fontSize: 15,
    fontWeight: 'normal',
    fontFamily: 'Source Sans Pro',
  },
  textDescriptionField: {
    marginBottom: 0,
  },
});

function LetsStart() {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = getStyles(theme);
  const query = useQuery();
  const type = query.get('type');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showEmailMsg, setShowEmailMsg] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [consentDocuments, setConsentDocuments] = useState([]);
  const [consentClientId, setConsentClientId] = useState(null);
  const [consentIndex, setConsentIndex] = useState(0);
  const [activeConsentField, setActiveConsentField] = useState(null);
  const [consentError, setConsentError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const emailRef = useRef(null);
  const consentIframeRef = useRef(null);
  const consentFormikRef = useRef(null);
  const consentResponseRef = useRef(null);
  const consentRequestRef = useRef(null);

  const handleAccessCodeClick = () => setIsOpen((v) => !v);
  const handleShowTerms = () => setShowTerms((v) => !v);
  const handleShowPrivacy = () => setShowPrivacy((v) => !v);
  const handleTermsLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleShowTerms();
  };
  const handlePrivacyLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleShowPrivacy();
  };

  const handleConsentCheckboxClick = async (event, formik, fieldName) => {
    if (window.location.hostname !== import.meta.env.VITE_DOMAIN_GENERIC) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (formik.values[fieldName]) {
      formik.setFieldValue(fieldName, false);
      return;
    }

    if (consentModalOpen) {
      return;
    }

    try {
      setIsLoading(true);
      setConsentError('');
      consentFormikRef.current = formik;
      if (!consentResponseRef.current) {
        consentRequestRef.current ??= apiService.amos
          .getConsents()
          .then(normalizeConsentDocuments);
        consentResponseRef.current = await consentRequestRef.current;
        consentRequestRef.current = null;
      }

      const { clientId, documents: allDocuments } = consentResponseRef.current;
      const documents = allDocuments.filter((document) =>
        fieldName === 'terms'
          ? /term/i.test(document.documentType)
          : /privacy|health/i.test(document.documentType)
      );

      if (!documents.length) {
        throw new Error(`No ${fieldName} consent document was returned.`);
      }

      setConsentClientId(clientId);
      setActiveConsentField(fieldName);
      setConsentDocuments(documents);
      setConsentIndex(0);
      setConsentModalOpen(true);
    } catch (error) {
      setConsentError('The consent documents could not be loaded.');
      consentRequestRef.current = null;
      consentFormikRef.current = null;
      setActiveConsentField(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsentFieldChange = (event, formik) => {
    if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    formik.handleChange(event);
  };

  const handleConsentFieldBlur = (event, formik) => {
    if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    formik.handleBlur(event);
  };

  const {
    clientCode,
    setIsLoading,
    country,
    countryDdi,
    fetchAmosCountry,
    loginAmos,
    userData,
  } = useContext(GeneralContext);
  const { locale, idiomAmos } = useTranslation({
    translationsFile: translations,
  });

  const {
    LABEL_ES,
    LABEL_EN,
    LABEL_PT,
    LABEL_FR,
    LABEL_DE,
    LABEL_IT,
    TITLE,
    FORM_TITLE,
    VALIDATION_REQUIRED,
    FIRSTNAME_FIELD,
    LASTNAME_FIELD,
    EMAILADDRESS_FIELD,
    DATEOFBIRTH_FIELD,
    GENDER_FIELD,
    MALE_LABEL,
    FEMALE_LABEL,
    LANGUAGE_FIELD,
    COUNTRY_RESIDENCE_FIELD,
    COUNTRY_CODE_FIELD,
    TELEPHONE_NUMBER_FIELD,
    ACCESS_CODE_FIELD,
    TERMS_AND_CONDITION_0,
    TERMS_AND_CONDITION_1,
    PRIVACY_NOTICE_0,
    PRIVACY_NOTICE_1,
    REGISTER_BUTTON,
    VALIDATION_MUST_BE_THAN_18,
    VALIDATE_ACCESS_CODE,
    EMAIL_ALREADY_EXISTS_TITLE_MESSAGE,
    EMAIL_ALREADY_EXISTS,
    REGISTRATION_CONFIRMATION_TITLE,
    REGISTRATION_CONFIRMATION_MESSAGE,
    VALIDATION_VALID_DATE,
    SELECT_A_COUNTRY,
    WHERE_TO_FIND_ACCESS_CODE,
    WHERE_TO_FIND_ACCESS_CODE_DESCRIPTION,
  } = locale;

  useEffect(() => {
    localStorage.setItem('userData', JSON.stringify(userData?.email));
  }, [userData]);

  let userEmail = localStorage.getItem('userData');
  if (userEmail != null) {
    userEmail = userEmail.replace(/"/g, '');
  }

  useEffect(() => {
    fetchAmosCountry(idiomAmos);
  }, [idiomAmos]);

  const initialValues = useMemo(
    () => ({
      clientCode: clientCode,
      firstName: '',
      lastName: '',
      day: '',
      month: '',
      year: '',
      gender: '',
      birthDate: null,
      email: userEmail ? userEmail : userData?.email,
      countryCode: '',
      cellphone: '',
      countryResidence: '',
      language: '',
      accessCode: '',
      terms: false,
      privacy: false,
    }),
    [userData, clientCode, userEmail]
  );

  const clCode = initialValues.clientCode;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(VALIDATION_REQUIRED),
    lastName: Yup.string().required(VALIDATION_REQUIRED),
    birthDate: Yup.date()
      .required(VALIDATION_REQUIRED)
      .min(new Date(1900, 0, 0), VALIDATION_VALID_DATE)
      .max(new Date(), VALIDATION_VALID_DATE)
      .test('age-validation', VALIDATION_MUST_BE_THAN_18, (item) => {
        const year = new Date().getFullYear();
        const age = parseInt(year) - parseInt(moment(item).format('yyyy'));
        return age >= 19;
      })
      .typeError(VALIDATION_VALID_DATE),
    gender: Yup.string().required(VALIDATION_REQUIRED),
    countryResidence: Yup.string().required(VALIDATION_REQUIRED),
    language: Yup.string().required(VALIDATION_REQUIRED),
    email: Yup.string().email().required(VALIDATION_REQUIRED),
    countryCode: Yup.string().required(VALIDATION_REQUIRED),
    cellphone: Yup.string().required(VALIDATION_REQUIRED),
    accessCode: Yup.string().required(VALIDATION_REQUIRED),
    terms: Yup.boolean().oneOf([true], VALIDATION_REQUIRED),
    privacy: Yup.boolean().oneOf([true], VALIDATION_REQUIRED),
  });

  const goToPatientSelection = () => {
    setShowConfirmation(false);
    if (type === 'user') {
      navigate(`/user`);
    } else {
      navigate(`/patient-selection?type=${type}`);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      zone: 'popup - account activated',
      element_clicked: 'ok',
      event: 'navigation',
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      subsidiary_name: 'axa_partners',
      business_unit: 'customer_and_solutions',
      business_line: 'travel_solutions',
      digital_business_process: 'assistance_request',
      global_digital_asset: 'doctor_please',
      partner_id: getPartnerId(),
      partner_name: getPartnerName(),
      country: getCountryCode(),
      language: getLanguage(),
      instance_environment: getInstanceEnv(),
      user_logged: 'true',
      page_name: 'patient selection',
      funnel_step: '1',
      step_name: 'request appointment',
      event: 'pageview',
    });
  };

  const handleSubmit = async (values, { setFieldError }) => {
    try {
      setIsLoading(true);
      const day = moment(values.birthDate).format('DD');
      const month = moment(values.birthDate).format('MM');
      const year = moment(values.birthDate).format('yyyy');
      const body = {
        client_code: clCode,
        name: values?.firstName,
        surname: values?.lastName,
        day: day,
        month: month,
        year: year,
        gender: values?.gender,
        country: values?.countryResidence,
        language: values?.language,
        email: values?.email,
        phone_prefix: values?.countryCode,
        phone: values?.cellphone,
        accessCode: values?.accessCode,
        client_id: consentClientId || undefined,
      };
      // if (window.location.hostname === process.env.REACT_APP_DOMAIN_GENERIC) {
      if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
        await apiService.amos.amosCreateB2BClient(body);
      } else {
        await apiService.amos.amosCreate(body);
      }
      await loginAmos();
      setShowConfirmation(true);
      setIsLoading(false);
      window.scrollTo(0, 0);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        form_name: 'register doctor please',
        event: 'form_success',
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'element_displayed ': 'popup',
        element_name: 'account activated',
        event: 'display',
      });
    } catch (err) {
      setIsLoading(false);

      let errorMsg =
        VALIDATE_ACCESS_CODE +
        'teleconsultation-support@axa-travel-insurance.com';
      if (err?.response?.data?.message) {
        const { message } = err.response.data;

        if (message.indexOf('430') >= 0 || message.indexOf('409') >= 0) {
          setShowEmailMsg(true);
          return;
        } else if (message.toLowerCase().includes('access code')) {
          setFieldError('accessCode', errorMsg);
          return;
        }
      }
      // Handle network or 502 errors
      if (
        err?.code === 'ECONNRESET' ||
        err?.errno === -4077 ||
        err?.response?.status === 502
      ) {
        setFieldError('accessCode', errorMsg);
        return;
      }

      // Fallback for any other error
      setFieldError('accessCode', errorMsg);
    }
  };

  const closeEmailMsg = () => {
    setShowEmailMsg(false);
  };

  useEffect(() => {
    if (!consentModalOpen) {
      return undefined;
    }

    const handleConsentMessage = (event) => {
      if (
        event.source !== consentIframeRef.current?.contentWindow ||
        event.data?.type !== CONSENT_MESSAGE_TYPE
      ) {
        return;
      }

      if (event.data.accepted === false) {
        setConsentError('You must accept the consent document to continue.');
        setConsentModalOpen(false);
        consentFormikRef.current = null;
        setActiveConsentField(null);
        return;
      }

      if (consentIndex + 1 < consentDocuments.length) {
        setConsentIndex((currentIndex) => currentIndex + 1);
        return;
      }

      consentFormikRef.current?.setFieldValue(activeConsentField, true);
      consentFormikRef.current = null;
      setActiveConsentField(null);
      setConsentModalOpen(false);
    };

    window.addEventListener('message', handleConsentMessage);
    return () => window.removeEventListener('message', handleConsentMessage);
  }, [consentModalOpen, consentDocuments, consentIndex, activeConsentField]);

  const isAmexDomain =
    window.location.hostname === import.meta.env.VITE_DOMAIN_AMEX;

  return (
    <>
      <ConsentIframeModal
        open={consentModalOpen}
        title={formatConsentTitle(consentDocuments[consentIndex]?.documentType)}
        description={`Document ${consentIndex + 1} of ${consentDocuments.length}`}
        iframeSrc={consentDocuments[consentIndex]?.url}
        iframeRef={consentIframeRef}
        onClose={() => {
          setConsentModalOpen(false);
          consentFormikRef.current = null;
          setActiveConsentField(null);
        }}
      />
      <ConfirmationModal
        open={Boolean(consentError)}
        title="Consent required"
        text={consentError}
        handleAction={() => setConsentError('')}
        okButton
      />
      <TermsAndConditions
        showTerms={showTerms}
        handleShowTerms={handleShowTerms}
      />
      <PrivacyPolicy
        showPrivacy={showPrivacy}
        handleShowPrivacy={handleShowPrivacy}
      />
      <ConfirmationModal
        open={showEmailMsg}
        title={EMAIL_ALREADY_EXISTS_TITLE_MESSAGE}
        text={EMAIL_ALREADY_EXISTS}
        handleAction={closeEmailMsg}
        okButton
      />
      <ConfirmationModal
        open={showConfirmation}
        title={REGISTRATION_CONFIRMATION_TITLE}
        text={REGISTRATION_CONFIRMATION_MESSAGE}
        handleAction={goToPatientSelection}
        okButton
      />
      <Container sx={classes.container}>
        <Grid size={{ xs: 12 }}>
          <Box component="p" sx={classes.title}>
            {TITLE}
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{ justifyContent: 'center', display: 'flex' }}
        >
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(formik) => (
              <Form>
                <Paper sx={classes.paperContent}>
                  <Grid sx={classes.gridSubTitle}>
                    <p style={classes.subTitle}>{FORM_TITLE}</p>
                  </Grid>
                  <Container data-testid="banner" sx={classes.containerInput}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <InputText name="firstName" label={FIRSTNAME_FIELD} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <InputText name="lastName" label={LASTNAME_FIELD} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <DatePicker
                          name="birthDate"
                          label={DATEOFBIRTH_FIELD}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <AutoComplete
                          name="gender"
                          label={GENDER_FIELD}
                          showPlaceholder
                          options={[
                            {
                              label: MALE_LABEL,
                              value: 'male',
                            },
                            {
                              label: FEMALE_LABEL,
                              value: 'female',
                            },
                          ]}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <InputText
                          name="email"
                          label={EMAILADDRESS_FIELD}
                          inputRef={emailRef}
                          disabled
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <DarkTitle label={TELEPHONE_NUMBER_FIELD} />
                        </Grid>
                        <Grid
                          container
                          className="d-flex align-items-center"
                          spacing={2}
                        >
                          <Grid size={{ xs: 3, md: 3 }}>
                            <AutoComplete
                              name="countryCode"
                              showPlaceholder
                              placeholder="+00"
                              options={countryDdi}
                            />
                          </Grid>
                          <Grid size={{ xs: 9, md: 9 }}>
                            <InputMask
                              name="cellphone"
                              mask={maskedCellphone}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <p>{COUNTRY_CODE_FIELD}</p>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <AutoComplete
                          name="countryResidence"
                          showPlaceholder
                          placeholder={SELECT_A_COUNTRY}
                          label={COUNTRY_RESIDENCE_FIELD}
                          options={country}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <AutoComplete
                          name="language"
                          label={LANGUAGE_FIELD}
                          showPlaceholder
                          options={[
                            { label: LABEL_ES, value: 'es' },
                            { label: LABEL_EN, value: 'en' },
                            { label: LABEL_PT, value: 'pt' },
                            { label: LABEL_FR, value: 'fr' },
                            { label: LABEL_DE, value: 'de' },
                            { label: LABEL_IT, value: 'it' },
                          ]}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <InputMask
                          name="accessCode"
                          label={
                            <span
                              style={{
                                ...classes.textDescriptionField,
                                display: 'block',
                              }}
                            >
                              {ACCESS_CODE_FIELD}
                              <a
                                onClick={handleAccessCodeClick}
                                style={classes.chevronDown}
                                id="accessCodeHelper"
                              >
                                {WHERE_TO_FIND_ACCESS_CODE}
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </a>
                              <div
                                style={
                                  isOpen
                                    ? classes.inputDescription
                                    : classes.inputDescriptionHide
                                }
                              >
                                {WHERE_TO_FIND_ACCESS_CODE_DESCRIPTION}
                              </div>
                            </span>
                          }
                          className="accessCodeInput"
                          mask={maskedAccessCode}
                          disabled={isAmexDomain}
                        />
                      </Grid>
                      <Grid
                        size={{ xs: 12, sm: 12, md: 12 }}
                        sx={classes.gridCheckbox}
                      >
                        <CheckBox
                          color="primary"
                          name="terms"
                          onClick={(event) =>
                            handleConsentCheckboxClick(event, formik, 'terms')
                          }
                          onChange={(event) =>
                            handleConsentFieldChange(event, formik)
                          }
                          onBlur={(event) =>
                            handleConsentFieldBlur(event, formik)
                          }
                          label={
                            <p style={classes.textCheckbox}>
                              {TERMS_AND_CONDITION_0}
                              <a
                                onClick={handleTermsLinkClick}
                                style={classes.linkCheckbox}
                              >
                                {TERMS_AND_CONDITION_1}
                              </a>
                            </p>
                          }
                        />
                      </Grid>
                      <Grid
                        size={{ xs: 12, sm: 12, md: 12 }}
                        sx={classes.gridCheckbox}
                      >
                        <CheckBox
                          color="primary"
                          name="privacy"
                          onClick={(event) =>
                            handleConsentCheckboxClick(event, formik, 'privacy')
                          }
                          onChange={(event) =>
                            handleConsentFieldChange(event, formik)
                          }
                          onBlur={(event) =>
                            handleConsentFieldBlur(event, formik)
                          }
                          label={
                            <p style={classes.textCheckbox}>
                              {PRIVACY_NOTICE_0}
                              <a
                                onClick={handlePrivacyLinkClick}
                                style={classes.linkCheckbox}
                              >
                                {PRIVACY_NOTICE_1}
                              </a>
                            </p>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Container>
                  <Grid container sx={classes.paddingFooter}>
                    <Button
                      type="submit"
                      text={REGISTER_BUTTON}
                      variant="contained"
                      color="primary"
                    />
                  </Grid>
                </Paper>
              </Form>
            )}
          </Formik>
        </Grid>
      </Container>
    </>
  );
}

export default LetsStart;
